/**
 * Optimized pagination component.
 *
 * Supports:
 * - Modern "Load more" pagination.
 * - Traditional pagination with infinite scroll enabled.
 * - Shopify Section Rendering requests.
 * - Near-viewport next-page prefetching.
 * - Shared response and in-flight request caches.
 * - AJAX quick-size initialization.
 */

window.collectionSectionCache ||=
  new Map();

window.collectionSectionPending ||=
  new Map();

if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;
      this.isFetching = false;
      this.controller = null;
      this.prefetchController = null;
      this.paginationObserver = null;
      this.prefetchObserver = null;
      this.fadeObserver = null;

      this.clickHandler =
        this.handleClick.bind(this);

      this.bfcacheHandler =
        this.reload.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;

      this.addEventListener(
        'click',
        this.clickHandler,
        true
      );

      document.addEventListener(
        'on:bfcache:load-restore',
        this.bfcacheHandler
      );

      this.init();
    }

    disconnectedCallback() {
      this.destroy();

      this.removeEventListener(
        'click',
        this.clickHandler,
        true
      );

      document.removeEventListener(
        'on:bfcache:load-restore',
        this.bfcacheHandler
      );

      this.isInitialized = false;
    }

    init() {
      this.infiniteScroll =
        this.dataset.infiniteScroll ===
        'true';
      if (this.classList.contains('main-products-grid')) this.infiniteScroll = true;

      this.pagination =
        this.querySelector(
          '.js-pagination'
        );

      this.loadMoreButton =
        this.querySelector(
          '.js-pagination-load-more'
        );

      this.results = [
        ...this.querySelectorAll(
          '.js-pagination-result'
        )
      ];

      this.isFetching = false;



      this.setupFadeObserver();
      if (this.dataset.priorityEnabled === 'true' && this.dataset.colorFilter === 'true' && !new URL(location.href).searchParams.has('page')) {
        if (this.priorityReady) { this.priorityUpdate(); this.setupPaginationBehavior(); }
        else this.priorityInit();
        return;
      }
      if (this.dataset.colorFilter === 'true') {
        // Preserve detached results and the advanced cursor on bfcache reload.
        if (!this.filteredBuffer) {
          this.filteredBuffer = [];
          let count = 0;
          this.results.forEach((item) => {
            if (item.hasAttribute('data-color-filter-product')) count++;
            if (count > 9) {
              item.remove();
              item.hidden = false;
              this.filteredBuffer.push(item);
            }
          });
          this.filteredNextUrl = this.pagination?.dataset.isMoreResults === 'true'
            ? this.loadMoreButton?.href : null;
        }
        this.results = [...this.querySelectorAll('.js-pagination-result')];
        this.updateFilteredCount();
        const hasMore = !!this.filteredNextUrl || this.filteredBuffer.length > 0;
        if (this.pagination) {
          this.pagination.hidden = !hasMore;
          this.pagination.dataset.isMoreResults = String(hasMore);
        }
        const count = this.querySelectorAll('[data-color-filter-product]').length;
        if (count < 9 && hasMore) this.loadMoreFiltered(9 - count);
      }
      if (!this.isFetching) this.setupPaginationBehavior();
    }

    destroy() {
      this.controller?.abort();
      this.prefetchController?.abort();

      this.controller = null;
      this.prefetchController = null;

      this.paginationObserver?.disconnect();
      this.prefetchObserver?.disconnect();
      this.fadeObserver?.disconnect();

      this.paginationObserver = null;
      this.prefetchObserver = null;
      this.fadeObserver = null;

      this.isFetching = false;
    }

    reload() {
      this.destroy();
      this.init();
    }

    setupPaginationBehavior() {
      if (this.dataset.colorFilter === 'true') {
        if (this.pagination && this.loadMoreButton &&
            this.pagination.dataset.isMoreResults === 'true' && !this.pagination.hidden) {
          this.setupInfiniteScroll();
        }
        return;
      }
      if (
        !this.pagination ||
        !this.loadMoreButton ||
        this.pagination.dataset
          .isMoreResults !== 'true' ||
        (this.results.length === 0 && this.dataset.colorFilter !== 'true')
      ) {
        return;
      }

      if (this.infiniteScroll) {
        this.setupInfiniteScroll();
      } else {
        this.setupPrefetch();
      }
    }

    setupInfiniteScroll() {
      if (
        !('IntersectionObserver' in window) ||
        !this.pagination
      ) {
        return;
      }

      this.paginationObserver?.disconnect();

      this.paginationObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (
                entry.isIntersecting &&
                !this.isFetching
              ) {
                this.loadMore();
              }
            });
          },
          {
            root: null,
            rootMargin: this.dataset.colorFilter === 'true' ? '200px 0px' : '700px 0px',
            threshold: 0
          }
        );

      this.paginationObserver.observe(
        this.pagination
      );
    }

    setupPrefetch() {
      if (
        !('IntersectionObserver' in window) ||
        !this.loadMoreButton
      ) {
        return;
      }

      this.prefetchObserver?.disconnect();

      this.prefetchObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              this.prefetchObserver
                ?.unobserve(entry.target);

              this.prefetchNextPage(
                entry.target.href
              );
            });
          },
          {
            root: null,
            rootMargin: '700px 0px',
            threshold: 0
          }
        );

      this.prefetchObserver.observe(
        this.loadMoreButton
      );
    }

    setupFadeObserver() {
      if (
        !('IntersectionObserver' in window) ||
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
      ) {
        return;
      }

      this.fadeObserver?.disconnect();

      this.fadeObserver =
        new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              entry.target.classList.add(
                'is-visible'
              );

              observer.unobserve(
                entry.target
              );
            });
          },
          {
            root: null,
            rootMargin:
              '0px 0px -20px 0px',
            threshold: 0
          }
        );
    }

    handleClick(event) {
      const loadMoreButton =
        event.target.closest(
          '.js-pagination-load-more'
        );

      if (
        loadMoreButton &&
        this.contains(loadMoreButton)
      ) {
        event.preventDefault();
        if (this.classList.contains('main-products-grid')) event.stopPropagation();
        this.loadMore();
        return;
      }

      const resultLink =
        event.target.closest(
          '.js-pagination-result a[href]'
        );

      if (
        resultLink &&
        this.contains(resultLink)
      ) {
        this.controller?.abort();
      }
    }

    getSectionId() {
      return (
        this.dataset.sectionId ||
        this
          .querySelector(
            '#filter-results'
          )
          ?.dataset.productsSectionId ||
        ''
      );
    }

    getRequestDetails(url) {
      const pageUrl = new URL(
        url,
        window.location.origin
      );

      pageUrl.searchParams.delete(
        'section_id'
      );

      const requestUrl = new URL(
        pageUrl.toString()
      );

      const sectionId =
        this.getSectionId();

      if (sectionId) {
        requestUrl.searchParams.set(
          'section_id',
          sectionId
        );
      }

      return {
        pageUrl,
        requestUrl,
        cacheKey:
          requestUrl.toString()
      };
    }

    async fetchPage(url, signal) {
      const details =
        this.getRequestDetails(url);

      const cachedHTML =
        window.collectionSectionCache.get(
          details.cacheKey
        );

      if (cachedHTML) {
        return {
          ...details,
          html: cachedHTML,
          fromCache: true
        };
      }

      let pendingRequest =
        window.collectionSectionPending.get(
          details.cacheKey
        );

      if (!pendingRequest) {
        pendingRequest = fetch(
          details.requestUrl.toString(),
          {
            method: 'GET',
            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            },
            signal
          }
        ).then(async (response) => {
          if (!response.ok) {
            throw new Error(
              `Pagination request failed: ${response.status}`
            );
          }

          return response.text();
        });

        window.collectionSectionPending.set(
          details.cacheKey,
          pendingRequest
        );
      }

      try {
        const html =
          await pendingRequest;

        if (
          window.collectionSectionCache
            .size >= 30
        ) {
          const oldestKey =
            window.collectionSectionCache
              .keys()
              .next().value;

          window.collectionSectionCache.delete(
            oldestKey
          );
        }

        window.collectionSectionCache.set(
          details.cacheKey,
          html
        );

        return {
          ...details,
          html,
          fromCache: false
        };
      } catch (error) {
        window.collectionSectionCache.delete(
          details.cacheKey
        );

        throw error;
      } finally {
        if (
          window.collectionSectionPending.get(
            details.cacheKey
          ) === pendingRequest
        ) {
          window.collectionSectionPending.delete(
            details.cacheKey
          );
        }
      }
    }

    prefetchNextPage(url) {
      if (!url) return;

      this.prefetchController?.abort();

      this.prefetchController =
        new AbortController();

      this.fetchPage(
        url,
        this.prefetchController.signal
      ).catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn(
            '[CustomPagination prefetch]',
            error
          );
        }
      });
    }

    getResultKey(result) {
      return (
        result.dataset.productId ||
        result
          .querySelector(
            '[data-product-id]'
          )
          ?.dataset.productId ||
        ''
      );
    }

    appendResults(newResults) {
      const lastResult =
        this.results[
          this.results.length - 1
        ];

      if (!lastResult) {
        throw new Error(
          'No existing pagination result found.'
        );
      }

      const existingKeys = new Set(
        this.results
          .map((result) =>
            this.getResultKey(result)
          )
          .filter(Boolean)
      );

      const appendedItems = [];

      const fragment =
        document.createDocumentFragment();

      newResults.forEach(
        (result, index) => {
          const resultKey =
            this.getResultKey(result);

          if (
            resultKey &&
            existingKeys.has(resultKey)
          ) {
            return;
          }

          if (resultKey) {
            existingKeys.add(resultKey);
          }

          result.classList.add(
            'pagination-fade-in'
          );

          result.style.transitionDelay =
            `${Math.min(index, 8) * 40}ms`;

          appendedItems.push(result);
          fragment.appendChild(result);
        }
      );

      lastResult.after(fragment);

      this.results = [
        ...this.querySelectorAll(
          '.js-pagination-result'
        )
      ];

      appendedItems.forEach((item) => {
        if (this.fadeObserver) {
          this.fadeObserver.observe(item);
        } else {
          item.classList.add(
            'is-visible'
          );
        }

        if (
          typeof window.initQuickSizeAreas ===
          'function'
        ) {
          window.initQuickSizeAreas(item);
        }
      });

      return appendedItems;
    }

    updatePagination(newPagination) {
      this.paginationObserver?.disconnect();
      this.prefetchObserver?.disconnect();

      if (
        newPagination &&
        newPagination.dataset
          .isMoreResults === 'true'
      ) {
        this.pagination.innerHTML =
          newPagination.innerHTML;

        this.loadMoreButton =
          this.pagination.querySelector(
            '.js-pagination-load-more'
          );

        this.setupPaginationBehavior();
        return;
      }

      this.pagination?.remove();
      this.pagination = null;
      this.loadMoreButton = null;

      document
        .querySelectorAll(
          '.js-when-paginated-only'
        )
        .forEach((element) => {
          element.remove();
        });
    }
    // Opt-in only: collection grids with Liquid color filtering enabled.
    updateFilteredCount() {
      if (this.dataset.colorFilter !== 'true') return;
      const count = this.querySelectorAll('[data-color-filter-product]').length;
      const message = this.pagination?.querySelector('.js-pagination-message');
      if (message) {
        const label = this.dataset.resultLabel || 'products';
        message.textContent = `Showing ${count} ${label}`;
      }
      const bar = this.pagination?.querySelector('.js-pagination-bar');
      if (bar) bar.hidden = true;
    }

    releaseFilteredBuffer(limit, response) {
            // Release exactly the requested number; retain overflow for the next click.
            const batch = [];
            let productCount = 0;
            while (this.filteredBuffer.length) {
              const item = this.filteredBuffer[0];
              if (item.hasAttribute('data-color-filter-product') && productCount >= limit) break;
              this.filteredBuffer.shift();
              if (item.hasAttribute('data-color-filter-product')) productCount++;
              item.hidden = false;
              batch.push(item);
            }
            let appended = [];
            if (batch.length) {
              if (this.results.length) appended = this.appendResults(batch);
              else {
                this.querySelector('[data-color-filter-grid]').append(...batch);
                appended = batch;
                this.results = [...this.querySelectorAll('.js-pagination-result')];
                batch.forEach((item) => {
                  item.classList.add('is-visible');
                  if (typeof window.initQuickSizeAreas === 'function') window.initQuickSizeAreas(item);
                });
              }
            }
      this.updateFilteredCount();
      if (appended.length) document.dispatchEvent(new CustomEvent('custom-pagination:appended', {
        bubbles: true,
        detail: { items: appended, pageUrl: response?.pageUrl.toString() || window.location.href, fromCache: response?.fromCache || false }
      }));
      return appended.filter((item) => item.hasAttribute('data-color-filter-product')).length;
    }

    async loadMoreFiltered(requestedCount = 9) {
      if (this.isFetching || this.dataset.pauseInfiniteScroll === 'true' ||
          !this.pagination || !this.loadMoreButton) return;
      this.filteredBuffer ||= [];
      if (this.filteredNextUrl === undefined) {
        this.filteredNextUrl = this.pagination.dataset.isMoreResults === 'true'
          ? this.loadMoreButton.href : null;
      }
      if (!this.filteredNextUrl && !this.filteredBuffer.length) return;
      this.isFetching = true;
      this.paginationObserver?.disconnect();
      this.prefetchObserver?.disconnect();
      const controller = new AbortController();
      this.controller = controller;
      const activeButton = this.loadMoreButton;
      activeButton.classList.add('is-loading');
      activeButton.setAttribute('aria-disabled', 'true');
      const visited = new Set();
      let failed = false;
      let lastResponse = null;
      let remainingCount = requestedCount;
      try {
        remainingCount -= this.releaseFilteredBuffer(remainingCount, null);
        while (remainingCount > 0 && this.filteredNextUrl) {
          if (controller.signal.aborted || !this.isConnected || this.dataset.pauseInfiniteScroll === 'true') return;
          const key = new URL(this.filteredNextUrl, window.location.origin).toString();
          if (visited.has(key)) throw new Error('Repeated pagination URL.');
          visited.add(key);
          const response = await this.fetchPage(key, controller.signal);
          if (controller.signal.aborted || !this.isConnected || this.dataset.pauseInfiniteScroll === 'true') return;
          const template = document.createElement('template');
          template.innerHTML = response.html;
          const source = [...template.content.querySelectorAll('custom-pagination')]
            .find((node) => node.dataset.colorFilter === 'true' && node.dataset.sectionId === this.dataset.sectionId);
          const grid = source?.querySelector('[data-color-filter-grid]');
          if (!source || !grid) throw new Error('Filtered collection response missing.');
          const pager = source.querySelector('.js-pagination');
          const next = pager?.querySelector('.js-pagination-load-more[href]');
          const hasMore = pager?.dataset.isMoreResults === 'true' && !!next;
          if (hasMore && visited.has(new URL(next.href, window.location.origin).toString())) throw new Error('Repeated next-page URL.');
          const keys = new Set([
            ...this.querySelectorAll('[data-color-filter-product]'),
            ...this.filteredBuffer.filter((item) => item.hasAttribute('data-color-filter-product'))
          ].map((item) => item.getAttribute('data-color-filter-product')));
          [...grid.querySelectorAll('.js-pagination-result')].forEach((item) => {
            const id = item.getAttribute('data-color-filter-product');
            if (id && keys.has(id)) return;
            if (id) keys.add(id);
            this.filteredBuffer.push(item);
          });
          this.filteredNextUrl = hasMore ? next.href : null;
          lastResponse = response;
          remainingCount -= this.releaseFilteredBuffer(remainingCount, response);
        }
      } catch (error) {
        failed = true;
        if (error.name !== 'AbortError') console.warn('[CustomPagination color filter]', error);
      } finally {
        if (this.controller === controller) {
          if (!controller.signal.aborted && this.isConnected && this.dataset.pauseInfiniteScroll !== 'true') {
            this.releaseFilteredBuffer(remainingCount, lastResponse);
            const hasMore = !!this.filteredNextUrl || this.filteredBuffer.length > 0;
            this.pagination.dataset.isMoreResults = String(hasMore);
            if (hasMore) {
              if (this.filteredNextUrl) activeButton.href = this.filteredNextUrl;
            } else {
              activeButton.remove();
              this.loadMoreButton = null;
              this.pagination.hidden = true;
            }
            this.updateFilteredCount();

          }
          this.controller = null;
          this.isFetching = false;
          activeButton.classList.remove('is-loading');
          activeButton.removeAttribute('aria-disabled');
          if (!failed && !controller.signal.aborted && this.isConnected &&
              this.dataset.pauseInfiniteScroll !== 'true') this.setupPaginationBehavior();
        }
      }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async priorityInit() {
      const grid = this.querySelector('[data-color-filter-grid]');
      if (!grid || !this.pagination || !this.loadMoreButton) {
        this.dataset.priorityEnabled = 'false';
        this.init();
        return;
      }
      const limit = Math.max(
        1,
        Math.min(300, Math.floor(Number(this.dataset.displayLimit) || 60))
      );
      const start = Math.max(1, Number(this.dataset.priorityStart) || 1);
      const end = Math.max(start, Number(this.dataset.priorityEnd) || 8);
      const original = [...grid.querySelectorAll('[data-color-filter-product]')];
      const all = [], seen = new Set();
      const collect = items => {
        for (const node of items) {
          if (all.length >= limit) break;
          const id = node.getAttribute('data-color-filter-product');
          if (id && !seen.has(id)) { seen.add(id); all.push(node); }
        }
      };
      collect(original);
      let next = this.pagination.dataset.isMoreResults === 'true' ? this.loadMoreButton.href : null;
      const visited = new Set();
      const controller = new AbortController();
      this.controller = controller;
      this.isFetching = true;
      const button = this.loadMoreButton;
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      try {
        while (all.length < limit && next) {
          const url = new URL(next, location.origin).toString();
          if (visited.has(url)) throw new Error('Repeated pagination URL');
          visited.add(url);
          const response = await this.fetchPage(url, controller.signal);
          if (controller.signal.aborted || !this.isConnected) return;
          const template = document.createElement('template');
          template.innerHTML = response.html;
          const source = [...template.content.querySelectorAll('custom-pagination')].find(n => n.dataset.sectionId === this.dataset.sectionId);
          const sourceGrid = source?.querySelector('[data-color-filter-grid]');
          if (!sourceGrid) throw new Error('Missing collection grid');
          collect(sourceGrid.querySelectorAll('[data-color-filter-product]'));
          const pager = source.querySelector('.js-pagination');
          const nextButton = pager?.querySelector('.js-pagination-load-more[href]');
          next = pager?.dataset.isMoreResults === 'true' && nextButton ? nextButton.href : null;
        }
        if (controller.signal.aborted || !this.isConnected) return;
        const from = Math.min(start - 1, all.length), to = Math.min(end, all.length);
        this.priorityBuffer = [...all.slice(from, to), ...all.slice(0, from), ...all.slice(to)];
        // First-page promo nodes stay in their original slots. Later pages' promo nodes are not duplicated.
        this.prioritySlots = original.map(node => {
          const marker = document.createComment('product-slot');
          node.before(marker); node.remove(); return marker;
        });
        this.priorityAnchor = document.createComment('end-of-products');
        const lastSlot = this.prioritySlots[this.prioritySlots.length - 1];
        if (lastSlot) lastSlot.after(this.priorityAnchor); else grid.append(this.priorityAnchor);
        this.priorityReady = true;
        this.priorityRelease();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[Priority collection]', error);
          this.dataset.priorityEnabled = 'false';
        }
      } finally {
        if (this.controller === controller) this.controller = null;
        this.isFetching = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        if (!controller.signal.aborted && this.isConnected) {
          if (this.priorityReady) { this.priorityUpdate(); this.setupPaginationBehavior(); }
          else if (this.dataset.priorityEnabled === 'false') this.init();
        }
      }
    }

    priorityRelease() {
      if (!this.priorityReady || !this.isConnected || this.dataset.pauseInfiniteScroll === 'true') return;
      const items = this.priorityBuffer.splice(0, 9);
      for (const item of items) {
        item.hidden = false;
        item.classList.add('is-visible');
        const slot = this.prioritySlots.shift();
        if (slot) slot.replaceWith(item); else this.priorityAnchor.before(item);
        if (typeof window.initQuickSizeAreas === 'function') window.initQuickSizeAreas(item);
      }
      this.results = [...this.querySelectorAll('.js-pagination-result')];
      this.priorityUpdate();
      if (items.length) document.dispatchEvent(new CustomEvent('custom-pagination:appended', {
        bubbles: true, detail: { items, pageUrl: location.href, fromCache: false }
      }));
      if (!this.isFetching && this.priorityBuffer.length) {
        this.paginationObserver?.disconnect();
        requestAnimationFrame(() => { if (this.isConnected && !this.isFetching) this.setupPaginationBehavior(); });
      }
    }

    priorityUpdate() {
      if (!this.pagination) return;
      const more = !!this.priorityBuffer?.length;
      this.pagination.hidden = !more;
      this.pagination.dataset.isMoreResults = String(more);
      this.updateFilteredCount();
    }

    async loadMore() {
      if (this.priorityReady) return this.priorityRelease();
      if (this.dataset.colorFilter === 'true') {
        return this.loadMoreFiltered();
      }
      if (
        this.isFetching ||
        this.dataset.pauseInfiniteScroll ===
          'true' ||
        !this.loadMoreButton ||
        !this.pagination
      ) {
        return;
      }

      this.isFetching = true;

      const activeButton =
        this.loadMoreButton;

      this.controller?.abort();

      this.controller =
        new AbortController();

      activeButton.classList.add(
        'is-loading'
      );

      activeButton.setAttribute(
        'aria-disabled',
        'true'
      );

      if (
        this.pagination.dataset
          .paginationStyle ===
        'traditional'
      ) {
        this.pagination.classList.add(
          'is-loading'
        );
      }

      try {
        const responseData =
          await this.fetchPage(
            activeButton.href,
            this.controller.signal
          );

        if (
          this.dataset.pauseInfiniteScroll ===
          'true'
        ) {
          throw new Error(
            'Pagination was interrupted.'
          );
        }

        const template =
          document.createElement(
            'template'
          );

        template.innerHTML =
          responseData.html;

        let responseRoot = template.content;
        if (this.classList.contains('main-products-grid')) {
          const candidates = [...template.content.querySelectorAll(this.localName)];
          responseRoot = candidates.find((node) =>
            (node.dataset.sectionId || node.querySelector('#filter-results')?.dataset.productsSectionId || '') === this.getSectionId()
          );
          if (!responseRoot) throw new Error('Matching product pagination component missing.');
        }
        const newResults = [
          ...responseRoot
            .querySelectorAll(
              '.js-pagination-result'
            )
        ];

        const newPagination =
          responseRoot.querySelector(
            '.js-pagination'
          );

        if (newResults.length === 0) {
          window.collectionSectionCache.delete(
            responseData.cacheKey
          );

          throw new Error(
            'No pagination results found in response.'
          );
        }

        const appendedItems =
          this.appendResults(
            newResults
          );

        this.updatePagination(
          newPagination
        );

        document.dispatchEvent(
          new CustomEvent(
            'custom-pagination:appended',
            {
              bubbles: true,
              detail: {
                items: appendedItems,
                pageUrl:
                  responseData.pageUrl
                    .toString(),
                fromCache:
                  responseData.fromCache
              }
            }
          )
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn(
            '[CustomPagination]',
            error
          );
        }
      } finally {
        this.controller = null;
        this.isFetching = false;

        activeButton.classList.remove(
          'is-loading'
        );

        activeButton.removeAttribute(
          'aria-disabled'
        );

        this.pagination?.classList.remove(
          'is-loading'
        );

        /*
         * 新分页仍在视口内时，
         * 重新触发无限滚动检查。
         */
        if (
          this.infiniteScroll &&
          this.pagination &&
          this.pagination.dataset
            .isMoreResults === 'true'
        ) {
          this.paginationObserver?.disconnect();

          window.requestAnimationFrame(
            () => {
              if (
                this.isConnected &&
                !this.isFetching
              ) {
                this.setupInfiniteScroll();
              }
            }
          );
        }
      }
    }
  }

  customElements.define(
    'custom-pagination',
    CustomPagination
  );
}